const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : fallback;
};

const defaultPlural = (name) => {
  const n = String(name || '').trim();
  if (!n) return '';
  return /s$|x$|z$|ch$|sh$/i.test(n) ? `${n}es` : `${n}s`;
};

const clampIndex = (i, length) => Math.max(0, Math.min(length - 1, i));

/* ── legacy helpers (preserved for backward compat) ────────────────────────── */

export function formatStock(pieces, piecesPerPack, packsPerBox) {
  if (pieces == null) return '0 pieces';
  if (piecesPerPack && packsPerBox) {
    const perBox = piecesPerPack * packsPerBox;
    const boxes = Math.floor(pieces / perBox);
    const afterBoxes = pieces % perBox;
    const packs = Math.floor(afterBoxes / piecesPerPack);
    const pcs = afterBoxes % piecesPerPack;
    const parts = [];
    if (boxes > 0) parts.push(`${boxes} box${boxes > 1 ? 'es' : ''}`);
    if (packs > 0) parts.push(`${packs} pack${packs > 1 ? 's' : ''}`);
    if (pcs > 0) parts.push(`${pcs} piece${pcs > 1 ? 's' : ''}`);
    return parts.length > 0 ? parts.join(', ') : '0 pieces';
  }
  return `${pieces} piece${pieces !== 1 ? 's' : ''}`;
}

export function formatStockShort(pieces, piecesPerPack, packsPerBox) {
  if (pieces == null) return '0';
  if (piecesPerPack && packsPerBox) {
    const perBox = piecesPerPack * packsPerBox;
    const boxes = Math.floor(pieces / perBox);
    const afterBoxes = pieces % perBox;
    const packs = Math.floor(afterBoxes / piecesPerPack);
    const pcs = afterBoxes % piecesPerPack;
    const parts = [];
    if (boxes > 0) parts.push(`${boxes}B`);
    if (packs > 0) parts.push(`${packs}Pk`);
    if (pcs > 0) parts.push(`${pcs}Pc`);
    return parts.length > 0 ? parts.join(' ') : '0';
  }
  return `${pieces}`;
}

export function getUnitLabel(unit) {
  switch (unit) {
    case 'PIECE': return 'Piece';
    case 'HALF_PACK': return 'Half Pack';
    case 'FULL_PACK': return 'Full Pack';
    case 'BOX': return 'Box';
    default: return unit || 'Piece';
  }
}

export function getUnitPrice(product, unit) {
  if (!unit || unit === 'PIECE') return product.salePrice || 0;
  if (String(unit).toUpperCase() === 'FULL_PACK') return product.pricePerFullPack || 0;
  if (String(unit).toUpperCase() === 'BOX') return product.pricePerBox || 0;
  if (String(unit).toUpperCase() === 'HALF_PACK') return product.pricePerHalfPack || 0;
  const units = getEffectiveUnits(product);
  const idx = units.findIndex((u) => u.name.toLowerCase() === String(unit).toLowerCase());
  if (idx >= 0) return units[idx].salePrice || 0;
  return product.salePrice || 0;
}

export function getPiecesForUnit(unit, qty, piecesPerPack, packsPerBox) {
  switch (unit) {
    case 'PIECE': return qty;
    case 'HALF_PACK': return qty * Math.ceil((piecesPerPack || 1) / 2);
    case 'FULL_PACK': return qty * (piecesPerPack || 1);
    case 'BOX': return qty * (piecesPerPack || 1) * (packsPerBox || 1);
    default: return qty;
  }
}

/* ── chain-aware helpers ───────────────────────────────────────────────────── */

export function getEffectiveUnits(product) {
  const packaging = product.packaging;
  const stored = Array.isArray(packaging?.units) && packaging.units.length > 0 ? packaging.units : null;
  if (stored) return stored;

  const baseName = String(packaging?.baseUnitName || 'Piece').trim() || 'Piece';
  const units = [{
    name: baseName,
    pluralName: defaultPlural(baseName),
    quantity: 1,
    baseFactor: 1,
    lowerUnitName: null,
    isBase: true,
    sellable: true,
    salePrice: product.salePrice || 0,
    purchasePrice: product.purchasePrice || 0,
  }];

  const ppp = Math.max(1, toNum(product.piecesPerPack, 1));
  const ppb = Math.max(1, toNum(product.packsPerBox, 1));

  if (ppp > 1) {
    units.push({
      name: 'Pack',
      pluralName: 'Packs',
      quantity: ppp,
      baseFactor: ppp,
      lowerUnitName: units[0].name,
      isBase: false,
      sellable: true,
      salePrice: product.pricePerFullPack || 0,
      purchasePrice: 0,
    });
  }
  if (ppb > 1) {
    const factor = ppp * ppb;
    units.push({
      name: 'Box',
      pluralName: 'Boxes',
      quantity: ppb,
      baseFactor: factor,
      lowerUnitName: units[units.length - 1].name,
      isBase: false,
      sellable: true,
      salePrice: product.pricePerBox || 0,
      purchasePrice: 0,
    });
  }

  return units;
}

export function resolveUnitIndex(units, unit) {
  if (!Array.isArray(units) || units.length === 0) return 0;
  if (unit === undefined || unit === null || unit === '') return 0;
  if (typeof unit === 'number') return clampIndex(Math.floor(unit), units.length);

  const s = String(unit).trim();
  if (s === '') return 0;

  const byName = units.findIndex((u) => String(u.name).toLowerCase() === s.toLowerCase());
  if (byName >= 0) return byName;

  switch (s.toUpperCase()) {
    case 'PIECE': return 0;
    case 'HALF_PACK': return -1;
    case 'FULL_PACK': return units.length > 1 ? 1 : 0;
    case 'BOX': return units.length - 1;
    default: return 0;
  }
}

export function convertToBase(units, unit, qty) {
  const quantity = Math.max(0, Math.floor(toNum(qty, 0)));
  if (quantity === 0) return 0;
  if (String(unit).toUpperCase() === 'HALF_PACK') {
    const fullPackFactor = units.length > 1 ? units[1].baseFactor : 0;
    return quantity * Math.max(1, Math.ceil((fullPackFactor || units[0].baseFactor) / 2));
  }
  const idx = resolveUnitIndex(units, unit);
  return quantity * (units[idx]?.baseFactor || 1);
}

export function decomposeStock(baseQty, units) {
  const safeUnits = Array.isArray(units) && units.length > 0
    ? units
    : [{ name: 'Piece', pluralName: 'Pieces', baseFactor: 1 }];
  const sorted = [...safeUnits].sort((a, b) => (b.baseFactor || 1) - (a.baseFactor || 1));
  let remainder = Math.max(0, Math.floor(toNum(baseQty, 0)));
  const parts = [];
  for (const u of sorted) {
    const factor = u.baseFactor || 1;
    if (factor <= 0) continue;
    const count = Math.floor(remainder / factor);
    if (count > 0) {
      parts.push({
        name: u.name,
        pluralName: u.pluralName || defaultPlural(u.name),
        count,
      });
      remainder -= count * factor;
    }
  }
  return parts;
}

export function formatStockFromUnits(pieces, units) {
  const parts = decomposeStock(pieces, units);
  if (parts.length === 0) {
    return `0 ${defaultPlural(units?.[0]?.name || 'piece')}`;
  }
  return parts
    .map((p) => `${p.count} ${p.count === 1 ? p.name : p.pluralName}`)
    .join(' + ');
}

export function formatStockShortFromUnits(pieces, units) {
  const parts = decomposeStock(pieces, units);
  if (parts.length === 0) return '0';
  return parts
    .map((p) => {
      const abbr = p.name.substring(0, 2);
      return `${p.count}${abbr}`;
    })
    .join(' ');
}

export function chainSummaryText(units) {
  const safeUnits = Array.isArray(units) && units.length > 0
    ? units
    : [{ name: 'Piece', pluralName: 'Pieces', baseFactor: 1 }];
  const lines = [];
  const seen = new Set();
  for (let i = safeUnits.length - 1; i >= 1; i -= 1) {
    const u = safeUnits[i];
    const lower = safeUnits.find((x) => String(x.name).toLowerCase() === String(u.lowerUnitName || '').toLowerCase())
      || safeUnits[i - 1];
    const plural = lower.pluralName || defaultPlural(lower.name);
    const line = `1 ${u.name} = ${u.quantity} ${plural}`;
    if (!seen.has(line)) {
      seen.add(line);
      lines.push(line);
    }
  }
  if (safeUnits.length > 1) {
    const top = safeUnits.reduce((a, b) => ((a.baseFactor || 1) >= (b.baseFactor || 1) ? a : b));
    const line = `1 ${top.name} = ${top.baseFactor} ${safeUnits[0].pluralName || defaultPlural(safeUnits[0].name)}`;
    if (!seen.has(line)) lines.push(line);
  }
  return lines;
}

export function getPiecesForUnitFromUnits(unit, qty, units) {
  const quantity = Math.max(0, Math.floor(toNum(qty, 0)));
  return convertToBase(units, unit, quantity);
}

/* ── stock guards ──────────────────────────────────────────────────────────── */

/** Base pieces currently on hand for a product (never negative). */
export function getAvailableStock(product) {
  const raw = product?.currentStock ?? product?.stock ?? product?.quantity ?? 0;
  return Math.max(0, Math.floor(toNum(raw, 0)));
}

/** How many whole `unit`s of this product can still be sold. */
export function getMaxUnitsFor(product, unit) {
  const units = getEffectiveUnits(product || {});
  const factor = Math.max(1, toNum(convertToBase(units, unit, 1), 1));
  return Math.floor(getAvailableStock(product) / factor);
}

/** Base pieces one cart line will consume (mirrors the backend rule). */
export function getLinePieces(item) {
  if (item?.piecesDeducted) return Math.max(0, Math.floor(toNum(item.piecesDeducted, 0)));
  return getPiecesForUnitFromUnits(item?.saleUnit, item?.quantity ?? 1, getEffectiveUnits(item || {}));
}

export function getUnitPriceFromUnits(product, unit) {
  const units = getEffectiveUnits(product);
  const idx = resolveUnitIndex(units, String(unit || '').toUpperCase() === 'HALF_PACK' ? undefined : unit);
  if (idx >= 0) {
    const price = units[idx].salePrice;
    if (price > 0) return price;
  }
  return product.salePrice || 0;
}

export function formatUnitsSummary(units) {
  const safeUnits = Array.isArray(units) && units.length > 0
    ? units
    : [{ name: 'Piece', quantity: 1, baseFactor: 1 }];
  return safeUnits
    .slice(1)
    .map((u) => `${u.quantity} ${u.name} = 1 ${u.name}`).join(', ');
}